// Client-side quality permission mirror (Module 07 §7, slices 07-A + 07-B).
//
// The server is the ONLY authorisation boundary: every §5 handler carries its
// `@RequiresPermission` code and the §7 narrowings (sales sees final-stage rows
// only, procurement receiving-stage only, a floor principal its own facility)
// are re-checked service-side on top of the single code — D-040's two-layer
// pattern. This hook exists so the UI does not OVER-SHOW controls a role cannot
// use.
//
// D-118 GOVERNS THE RENDERING, not just the grant. A control this principal may
// not use is ABSENT, never disabled-without-reason: a dead "New control point"
// button teaches a planner that the product is broken. Where a control is dead
// for a REASON THE OPERATOR CAN FIX (a completion still missing three verdicts)
// it stays and says so — that is the other half of the same rule.
//
// `has()` reads the permission list `GET /v1/auth/tenants` carries (D-586), so a
// tenant's clone of a seed role is offered what it holds. The table below
// mirrors `systemRoleGrants` in packages/contracts, transcribed cell by cell
// from §7's action matrix, and answers only for a feed row without that list.
// `has()` is FAIL-CLOSED — false until the feed settles, false if it fails.

import {
  derivePermissions,
  hasLineage,
  roleSignalOf,
  useRoleCodesFeed,
  type AreaPermissions,
  type RoleInput,
  type RoleSignal,
} from "../../../../lib/role-codes";

/**
 * The `quality.*` codes slices 07-A and 07-B register (spec 07 §7; the
 * register's own permission notes in packages/contracts/src/quality/register.ts).
 *
 * 07-B's five (routes live since 07-B-1/07-B-2): the two resolution codes on
 * `POST /inspections/{id}/resolve` (D-040 two-layer — `dispose` is
 * service-enforced on top for scrap/accept_deviation), the manual-hold placer,
 * the disposition code on `POST /holds/{id}/disposition` (procurement narrowed
 * to return_to_supplier service-side), and `quality.hold.read` — the fifth
 * code D-128 r3 added because §5's holds role set matches no other.
 *
 * 07-C's one, `quality.cut.execute`: the §7 "Guided cut" row, carried by all
 * FIVE §5 cut routes including the detail read (D-163 — one code per DISTINCT
 * role set, one code per route). CANCEL is narrowed SERVICE-side to
 * {group_admin, facility_admin, qc}, which is why floor_worker holds the code
 * here rather than a second one — the screen that offers a cancel mirrors that
 * narrowing the way `offeredQuarantineDispositions` mirrors procurement's
 * (07-C's floor slice; the code itself gates the surface's EXISTENCE).
 *
 * 07-E's CoA three, mirrored WITH their routes (the same handover every
 * earlier slice made): `quality.coa.draft` (fa/qc/sales + ga — templates
 * list, documents list, draft, preview), `quality.coa.issue` (qc + ga —
 * issue AND revoke, one authority) and `quality.coa_template.manage`
 * (ga/fa/qc). `quality.analytics.read` joined WITH the five analytics
 * routes (0046) — the catalog's seventeen §7 codes are all mirrored now, and
 * the per-route §5 cells (floor_worker: scoreboard only; procurement:
 * supplier panel only) are the SERVER's service-layer narrowings; the §4.8
 * screen reads the same cells from `qualityAnalyticsCells` below.
 */
export const QUALITY_PERMISSIONS = [
  "quality.catalog.read",
  "quality.catalog.manage",
  "quality.vertical_pack.seed",
  "quality.inspection.read",
  "quality.inspection.create",
  "quality.inspection.record",
  "quality.inspection.cancel",
  "quality.resolution.rework",
  "quality.resolution.dispose",
  "quality.hold.place",
  "quality.hold.disposition",
  "quality.hold.read",
  "quality.cut.execute",
  "quality.coa.draft",
  "quality.coa.issue",
  "quality.coa_template.manage",
  "quality.analytics.read",
] as const;

export type QualityPermission = (typeof QUALITY_PERMISSIONS)[number];

/**
 * Role code → `quality.*` codes: the quality slice of the contracts
 * `systemRoleGrants` table (spec 07 §7), transcribed cell by cell.
 *
 * The rows that are easy to get wrong:
 * - `maintenance` holds NOTHING. Every one of §7's quality cells for it is "—",
 *   and an empty array states that rather than leaving the role out of the map
 *   (where it would read as an oversight).
 * - `finance` and `sales` hold `quality.inspection.read` and NOT
 *   `quality.catalog.read`: §7's control-points read row is "—" for both. So
 *   finance can see the queue and cannot see the definitions — which is why the
 *   area's entry table below can put a role on one screen and not the other.
 *   Both DO hold `quality.hold.read` (07-B): sales sees blocked units for
 *   promised orders, finance sees the money tied up (§4.6's read roles).
 * - `storekeeper` may CREATE an inspection and PLACE a quarantine (§7 "Holds:
 *   place manual" is ✓) but may not RECORD results, READ the board or
 *   DISPOSE: the physical act is the inspector's and the decision is qc's.
 * - `planner` reads both, reads the board, and holds exactly ONE write:
 *   `quality.resolution.rework` (§7 "Resolve: rework") — scrap and
 *   accept_deviation ride `quality.resolution.dispose`, which is qc-only.
 * - `procurement` holds `quality.hold.disposition` for ONE kind —
 *   return_to_supplier — a narrowing the SERVER enforces on top of the code
 *   (D-040); `offeredQuarantineDispositions` below mirrors it so the dialog
 *   does not offer four kinds that would 403.
 * - `facility_admin` may resolve REWORK and place a hold, but holds NO
 *   disposition code: §7's "Holds: disposition" row is qc + procurement only.
 */
export const QUALITY_ROLE_GRANTS: Readonly<Record<string, readonly string[]>> = {
  group_admin: [...QUALITY_PERMISSIONS],
  facility_admin: [
    "quality.catalog.read",
    "quality.catalog.manage",
    "quality.inspection.read",
    "quality.inspection.create",
    "quality.inspection.record",
    "quality.inspection.cancel",
    "quality.resolution.rework",
    "quality.hold.place",
    "quality.hold.read",
    "quality.cut.execute",
    "quality.coa.draft",
    "quality.coa_template.manage",
    "quality.analytics.read",
  ],
  planner: [
    "quality.catalog.read",
    "quality.inspection.read",
    "quality.resolution.rework",
    "quality.hold.read",
    "quality.analytics.read",
  ],
  sales: ["quality.inspection.read", "quality.hold.read", "quality.coa.draft"],
  procurement: [
    "quality.catalog.read",
    "quality.inspection.read",
    "quality.hold.disposition",
    "quality.hold.read",
    "quality.analytics.read",
  ],
  finance: ["quality.inspection.read", "quality.hold.read", "quality.analytics.read"],
  qc: [
    "quality.catalog.read",
    "quality.catalog.manage",
    "quality.inspection.read",
    "quality.inspection.create",
    "quality.inspection.record",
    "quality.inspection.cancel",
    "quality.resolution.rework",
    "quality.resolution.dispose",
    "quality.hold.place",
    "quality.hold.disposition",
    "quality.hold.read",
    "quality.cut.execute",
    "quality.coa.draft",
    "quality.coa.issue",
    "quality.coa_template.manage",
    "quality.analytics.read",
  ],
  maintenance: [],
  storekeeper: [
    "quality.catalog.read",
    "quality.inspection.read",
    "quality.inspection.create",
    "quality.hold.place",
  ],
  floor_worker: [
    "quality.catalog.read",
    "quality.inspection.read",
    "quality.inspection.create",
    "quality.inspection.record",
    // §7's "Guided cut" cell is "✓ (not cancel)" — the person standing at the
    // saw proposes, confirms and executes. The "not cancel" half is a SERVICE
    // narrowing on top of this one code (D-163), not a second code.
    "quality.cut.execute",
    // 07-E: §5's scoreboard cell — the server refuses the other three
    // analytics reads service-side; the screen mirrors the same cell.
    "quality.analytics.read",
  ],
};

// ---------------------------------------------------------------------------
// §3.5c's disposition kinds, and WHO is offered WHICH (D-040's two layers,
// mirrored so the dialog never offers a kind the server would refuse).
// ---------------------------------------------------------------------------

/** §2.7's disposition vocabulary — what happens to HELD STOCK. */
export const QUARANTINE_DISPOSITIONS = [
  "release",
  "rework",
  "scrap",
  "downgrade",
  "return_to_supplier",
] as const;

export type QuarantineDisposition = (typeof QUARANTINE_DISPOSITIONS)[number];

/**
 * The kinds THIS principal may take on the board (§5's disposition row: "qc
 * (all), procurement (return_to_supplier only)"; group_admin through the spec
 * 16 §2.8 invariant). The single code `quality.hold.disposition` gates the
 * control's EXISTENCE; this narrows the dialog's kind list, mirroring the
 * server's D-040 service-side check so procurement is not offered four kinds
 * that 403. A failed role feed offers everything and lets the server refuse
 * (the mirror is a display aid, never the boundary); a custom role resolves
 * to nothing, same as every other write control in this file.
 */
export function offeredQuarantineDispositions(
  perms: RoleSignal & Pick<QualityPermissions, "isFeedError">,
): readonly QuarantineDisposition[] {
  if (perms.isFeedError) return QUARANTINE_DISPOSITIONS;
  // The server keys this on lineage (`HOLD_DECISION_ROLES`,
  // `refusal-continuations.ts`), so a tenant's clone of qc decides as qc does
  // and a clone of procurement stays confined (D-224, D-586).
  if (hasLineage(perms, ["group_admin", "qc"])) return QUARANTINE_DISPOSITIONS;
  if (hasLineage(perms, ["procurement"])) return ["return_to_supplier"];
  return [];
}

/** The effective `quality.*` set (pure, unit-tested): the feed's permission
 * list cut to this slice, or the mirror without one (D-586). */
export function deriveQualityPermissions(roles: RoleInput): Set<string> {
  return derivePermissions(roles, QUALITY_ROLE_GRANTS);
}

export interface QualityPermissions extends AreaPermissions, RoleSignal {
  readonly roleCodes: ReadonlySet<string>;
  /** True when the role feed itself failed — the screen says so and lets the
   * request go, rendering the server's own refusal rather than a blank page. */
  readonly isFeedError: boolean;
}

export function useQualityPermissions(): QualityPermissions {
  const feed = useRoleCodesFeed();
  const codes = deriveQualityPermissions(feed);
  return {
    isResolved: feed.isResolved,
    has: (code: string) => codes.has(code),
    ...roleSignalOf(feed),
    isFeedError: feed.isFeedError,
  };
}

/**
 * Is this principal REFUSED a quality read — a stable fact, not a failure?
 *
 * True only once the role feed has SETTLED and answered. While it is in flight,
 * or when it FAILED, the answer is "let the request go and render the server's
 * own refusal" — the fail-open direction, because the mirror is a display aid
 * and the server is the authorisation boundary. A read surface must never turn
 * "we cannot tell" into "you are not permitted" (role-codes.ts's rule; module
 * 05's `isWasteReadRefused` is the same predicate).
 *
 * THE ONE DELIBERATE EXCEPTION IS MONEY (fix round R8, stated rather than
 * aligned): §4.3's value-at-risk chip column gates on a bare
 * `perms.has("quality.hold.read")` (inspections.tsx `canSeeAtRisk`), which is
 * FAIL-CLOSED — false while the feed is unresolved and false when it FAILED.
 * That is the right direction for exactly this control and no other: the
 * fail-open rule exists so a feed outage cannot turn into a false "you are
 * not permitted" on a working READ SURFACE, but the chip column is not a
 * surface anyone is refused — the queue renders fully without it — and the
 * asymmetry of errors flips. Failing open shows a money column on the word
 * of a mirror that could not answer; failing closed costs a redundant aid
 * for the length of an outage (the quarantine board, the authoritative money
 * screen, still answers with the server's own §7 redaction). Do not "fix"
 * this to match the rule above.
 *
 * Callers use it twice: to keep a read that will be refused from being ISSUED
 * at all, and to render `ForbiddenState` instead of an error card whose Retry
 * can never succeed.
 */
export function isQualityReadRefused(perms: QualityPermissions, code: QualityPermission): boolean {
  return perms.isResolved && !perms.isFeedError && !perms.has(code);
}

// ---------------------------------------------------------------------------
// THE AREA'S ENTRY TABLE — one rule, read by the app nav AND the sub-nav.
// ---------------------------------------------------------------------------
//
// Module 05 shipped this area with TWO gating rules — the app nav's and the
// sub-nav's — and five roles that own `waste.*` codes ended up with no link
// into the area at all. That defect is written up at length in
// `areas/office/waste/hooks/permissions.ts`; this table is the fix applied
// BEFORE the defect rather than after it. `qualityAreaEntries` IS the rule: the
// sub-nav renders it in order, the app nav shows a link iff it is non-empty and
// points at its FIRST entry, and `/quality` redirects there.
//
// ORDER IS THE LANDING ORDER, and it is deliberate: the QUEUE first — the
// screen that answers "what needs checking" without an id in hand, and the
// screen every §7 read role can open — then the QUARANTINE BOARD (§4.6,
// 07-B-3: what is held and what happens to it; the one quality screen sales
// and finance can open beside the queue), then the definition library, which
// is where you go on purpose and never where you arrive.

export interface QualityAreaEntry {
  /** The route path. */
  readonly to: string;
  /** The dictionary key for its label (`quality.nav.*`). */
  readonly labelKey: string;
}

/** The permission facts the entry table reads — the shape both callers hold. */
export interface QualityEntryPermissions {
  readonly has: (code: string) => boolean;
}

/** The quality screens this principal may open, in landing order (pure). */
export function qualityAreaEntries(perms: QualityEntryPermissions): QualityAreaEntry[] {
  const entries: QualityAreaEntry[] = [];
  if (perms.has("quality.inspection.read")) {
    entries.push({ to: "/quality/inspections", labelKey: "quality.nav.inspections" });
  }
  if (perms.has("quality.hold.read")) {
    entries.push({ to: "/quality/quarantine", labelKey: "quality.nav.quarantine" });
  }
  if (perms.has("quality.analytics.read")) {
    // 07-E §4.8 — analytics after the board: the numbers people check daily.
    entries.push({ to: "/quality/analytics", labelKey: "quality.nav.analytics" });
  }
  if (perms.has("quality.coa.draft")) {
    // 07-E §4.7 — after the operational screens, before the definition
    // library: a certificate is something you go make, not where you arrive.
    entries.push({ to: "/quality/coa", labelKey: "quality.nav.coa" });
  }
  if (perms.has("quality.catalog.read")) {
    entries.push({ to: "/quality/control-points", labelKey: "quality.nav.controlPoints" });
    // §4.1b, the failure-cause catalog (fix #117). LAST, and behind the same
    // code as the library: it is the other half of "what this factory checks
    // and what it calls a defect", and — since D-132 — the only place a qc can
    // see WHICH causes will refuse a scrap and do something about it. The list
    // read itself carries no permission code (§5 row 7, any authenticated
    // principal), but the SCREEN is a catalogue screen, so it enters the area
    // on the catalogue's read code rather than inventing a second rule for a
    // nav strip that already has exactly one.
    entries.push({ to: "/quality/failure-causes", labelKey: "quality.nav.failureCauses" });
  }
  return entries;
}

/** Where `/quality` (and the app-nav link) lands this principal, or `undefined`
 * when the area holds nothing for them — `maintenance`, and any custom role the
 * mirror cannot resolve. */
export function qualityLandingPath(perms: QualityEntryPermissions): string | undefined {
  return qualityAreaEntries(perms)[0]?.to;
}
